<script setup lang="ts">
import { api } from '@/api';
import { AppPermissionsEnum, JobConfigurationDto } from '@/api/generator';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import { computed, ref, watchEffect, useTemplateRef } from 'vue';
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import { routenames } from '@/router/routenames';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import EntityChange from '@/components/EntityHistory/EntityChange.vue';
import FirstEntityFeedback from '@/components/PageFeedback/FirstEntityFeedback.vue';
import { useLoading } from '@/utils/useLoading';
import PtwAddButton from '@/components/buttons/PtwAddButton.vue';
import { useDeleteJobConfigurationWithConfirmation } from './utils';
import {
  globalContextMenuHeaderItemDefinition,
  getDefaultContextMenuActions,
} from '@/components/ContextMenu/utils';
import JobConfigurationOverviewFilter from './components/JobConfigurationOverviewFilter.vue';
import CommentCount from '@/components/forms/CommentCount/CommentCount.vue';
import type { JobOverviewFilterObject } from './types';
import { usePermissionsStore } from '@/stores/permissionsStore';
import { useTranslation } from 'i18next-vue';
import { ContextMenuAction } from '@/components/ContextMenu/types';
import { useRouter } from 'vue-router';
import { sectionJob } from '@/components/icons/icons.ts';
import { I18nKey } from '@/utils/typings';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import { buildOdataQuery } from '@/utils/odata';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils';
import OverviewTableHeader from '@/components/OverviewTable/OverviewTableHeader.vue';
import { compareFrequencyType, localizedEnumCompare } from '@/utils/compareAndSort';
import { isEmpty } from 'lodash';
import AssignedUsersRoles from '@/components/AssignedUsersRoles/AssignedUsersRoles.vue';
import { SortItem } from '@/components/OverviewTable/types';
import { EntityInfo } from '@/components/confirmationModals/types';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import { TAB_PREFIX } from '@/components/confirmationModals/useFormConfirmationModal';
import { getEntityChangeEvent } from '@/components/EntityHistory/entityChange';
import { useRemainingViewportHeight } from '@/utils/useRemainingVueport';
import { useTableScrollElement } from '@/utils/vuetifyMenuHelper';

const { t } = useTranslation();
const tableRef = useTemplateRef<HTMLDivElement>('table');
const { height } = useRemainingViewportHeight(tableRef);
const tableScrollElement = useTableScrollElement(tableRef);

const router = useRouter();

const expanded = ref<string[]>([]);

const headers = computed(
  () =>
    [
      {
        title: t('Name'),
        key: 'name',
      },
      {
        title: t('Description'),
        key: 'description',
        width: '20%',
      },
      {
        title: t('MedicalSystem_one'),
        key: 'targetSystem.name',
        // sort alphabetically by name
      },
      {
        title: t('StateOfUse'),
        key: 'state',
        sort: localizedEnumCompare('StateOfUseEnum'),
      },
      {
        title: t('Protocol_other'),
        key: 'protocols',
        sortable: false,
      },
      {
        title: t('Recurrence' as I18nKey),
        key: 'schedule.frequency',
        sort: compareFrequencyType,
        // sort by order of frequency length
      },
      {
        title: t('NumActiveWorkItems' as I18nKey),
        key: 'activeTaskInJobCount',
      },
      {
        title: t('Assignment'),
        key: 'assignment',
        sortable: false,
      },
      {
        title: t('LastModification'),
        key: 'lastModificationTime',
      },
      globalContextMenuHeaderItemDefinition,
    ] as const,
);

const jobConfigurations = ref<JobConfigurationDto[]>();

const filterObject = ref<JobOverviewFilterObject>({});

const canAddJobs = usePermissionsStore().hasPermissions([
  AppPermissionsEnum.GlobalJobConfigurationsAdd,
]);

const {
  action: loadJobConfigurations,
  loading,
  loadingFirstTime,
} = useLoading(async (filter: JobOverviewFilterObject = {}) => {
  const { result } = await api.jobConfigurationsApi.getJobConfigurationList(
    buildOdataQuery({
      search: filter.search,
      filter: {
        targetSystem: {
          id: { in: filter.targetSystemIds },
        },
        schedule: {
          frequency: { eq: filter.frequency },
        },
        protocols: filter.protocolIds?.length
          ? {
              any: {
                id: { in: filter.protocolIds },
              },
            }
          : undefined,
      },
    }),
  );

  jobConfigurations.value = result?.value || undefined;

  return jobConfigurations.value;
}, true);

const initialData = ref<JobConfigurationDto[]>();

watchEffect(async () => {
  const updatedJobConfigurations = await loadJobConfigurations(filterObject.value);

  if (isEmpty(filterObject.value)) {
    initialData.value = updatedJobConfigurations;
  }
});

const { action: deleteAction } = useDeleteJobConfigurationWithConfirmation();

function getContextMenuActions(item: JobConfigurationDto): ContextMenuAction[] {
  const defaultContextMenuActions = getDefaultContextMenuActions(item);
  return [
    {
      ...defaultContextMenuActions.view,
      to: {
        name: routenames.jobConfigurations.details,
        params: { id: item.id },
      },
    },
    {
      ...defaultContextMenuActions.edit,
      to: {
        name: routenames.jobConfigurations.edit,
        params: { id: item.id },
      },
    },
    {
      ...defaultContextMenuActions.copy,
      to: {
        name: routenames.jobConfigurations.copy,
        params: { copyId: item.id },
      },
    },
    {
      ...defaultContextMenuActions.delete,
      callback: async () => {
        const defaultConfirmationOptions = getDefaultDeleteConfirmationOptions(
          {
            ...(item as EntityInfo),
            typeName: 'JobConfiguration_one',
          },
          t,
        );

        const { result } = await deleteAction({
          param: item.id,
          confirmationOptions: {
            ...defaultConfirmationOptions,
            size: 'large',
            contentData: {
              ...defaultConfirmationOptions.contentData,
              hint: t('JobConfigurationDeletionEffectsMsg', {
                hint: defaultConfirmationOptions.contentData.hint,
              }),
            },
          },
        });

        if (result?.success) {
          loadJobConfigurations(filterObject.value);
        }
      },
    },
  ];
}

function navigateTo(jobId: string, hash?: string) {
  router.push({
    name: routenames.jobConfigurations.details,
    hash,
    params: { id: jobId },
  });
}

function getProtocolNamesList(job: JobConfigurationDto) {
  return job.protocols?.map((p) => p.name).join(', ');
}

function getTaskInJobNumbers(job: JobConfigurationDto) {
  return job.tasks && job.tasks.length > 0
    ? `${job.activeTaskInJobCount} / ${job.tasks.length}`
    : EMPTY_FIELD_SIGN;
}

const showFirstEntityFeedback = computed(
  () => !loading.value && jobConfigurations.value?.length === 0 && isEmpty(filterObject.value),
);

const defaultSorting: SortItem[] = [{ key: 'name', order: 'asc' }];
</script>

<template>
  <BasePage :loading="loadingFirstTime">
    <template #title>
      <BasePageTitle :title="$t('JobConfiguration_other')" />
    </template>

    <template #actions>
      <PtwAddButton
        v-if="canAddJobs && jobConfigurations?.length"
        :to="{ name: routenames.jobConfigurations.add }"
      />
    </template>

    <JobConfigurationOverviewFilter
      v-if="!showFirstEntityFeedback"
      v-model="filterObject"
      :initialData="initialData"
    />

    <OverviewTable
      v-if="!showFirstEntityFeedback"
      ref="table"
      v-model:expanded="expanded"
      class="mt-2"
      :headers="headers"
      :items="jobConfigurations"
      :loading="loading"
      itemLabel="JobConfiguration"
      data-testid="DataTable_Jobs"
      :sortBy="defaultSorting"
      :height="height"
      fixedHeader
      @click:row="
        (_event, data) => {
          navigateTo(data.item.id);
        }
      "
    >
      <template #header.name="headerProps">
        <OverviewTableHeader v-bind="headerProps">
          {{ $t('Name') }}
          <span class="font-weight-medium">| {{ $t('ID') }}</span>
        </OverviewTableHeader>
      </template>

      <template #item.name="{ item }">
        <IdentifierLabel :label="item.name" :identifier="item.iuid" />
        <CommentCount :value="item" @click.stop="navigateTo(item.id, `#${TAB_PREFIX}comments`)" />
      </template>

      <template #item.targetSystem.name="{ item }">
        {{ item.targetSystem?.name || EMPTY_FIELD_SIGN }}
      </template>

      <template #item.protocols="{ item }">
        {{ getProtocolNamesList(item) || EMPTY_FIELD_SIGN }}
      </template>

      <template #item.activeTaskInJobCount="{ item }">
        {{ getTaskInJobNumbers(item) || EMPTY_FIELD_SIGN }}
      </template>

      <template #item.assignment="{ item }">
        <AssignedUsersRoles :users="item.assignedUsers" :roles="item.assignedRoles" />
      </template>

      <template #item.lastModificationTime="{ item }">
        <EntityChange :entityChange="getEntityChangeEvent(item, 'modified')" />
      </template>

      <template #item.state="{ item }">
        {{ $t(`StateOfUseEnum.${item.state}` as I18nKey) }}
        <EntityChange
          :entityChange="getEntityChangeEvent(item, 'editing')"
          :showEmptyFieldSign="false"
        />
      </template>

      <template #item.schedule.frequency="{ item }">
        {{
          item.schedule?.frequency
            ? $t(`FrequencyType.${item.schedule.frequency}` as I18nKey)
            : EMPTY_FIELD_SIGN
        }}
      </template>

      <template #item.contextMenu="{ item }">
        <ContextMenu
          :actions="getContextMenuActions(item)"
          :closeOnScrollElements="tableScrollElement"
        />
      </template>
    </OverviewTable>

    <FirstEntityFeedback
      v-if="showFirstEntityFeedback"
      :permission="AppPermissionsEnum.GlobalJobConfigurationsAdd"
      :noPermissionMessage="$t('NoJobsConfigured')"
      :actionText="$t('AddJobEntity')"
      :icon="sectionJob"
      :to="{ name: routenames.jobConfigurations.add }"
    />
  </BasePage>
</template>
